import React, { useState } from "react";
import { api } from "../api/client.js";
import { colors } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn, linkBtn } from "../clients/ui.js";
import { runLimited } from "../clients/bulkTasks.js";
import { parseCsv, autoMap, templateCsv, buildPreview, CONTACT_FIELDS, TRACK_FIELDS, FIELD_LABELS } from "./csv.js";
import Modal from "./Modal.jsx";

const KIND = {
  new: { label: "New", color: "#6ee7b7", bg: "rgba(16,185,129,0.15)" },
  existing: { label: "Already in book", color: "#93c5fd", bg: "rgba(59,130,246,0.15)" },
  problem: { label: "Problem", color: "#fca5a5", bg: "rgba(239,68,68,0.12)" },
};

const readText = (file) => (file.text ? file.text() : new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(String(r.result || "")); r.onerror = rej; r.readAsText(file);
}));

// Import contacts from a CSV: choose file → match columns → preview → import. With a clientId,
// every imported row also gets a track on that client. onDone(summary) receives e.g.
// "8 added, 2 linked, 1 skipped".
export default function ImportCsv({ clientId = null, clientName = "", contacts = [], users = [], clientTracks = [], onDone, onClose }) {
  const withTrack = !!clientId;
  const [step, setStep] = useState("file");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fields = withTrack ? [...CONTACT_FIELDS, ...TRACK_FIELDS] : CONTACT_FIELDS;

  const [dragging, setDragging] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setError(null);
    if (!/\.csv$/i.test(file.name || "") && file.type !== "text/csv") return setError("That isn’t a CSV file. Save it as .csv from Excel (File → Save As → CSV) and try again.");
    const parsed = parseCsv(await readText(file));
    if (parsed.length < 2) return setError("That file has no rows under the header.");
    setHeaders(parsed[0]);
    setRows(parsed.slice(1));
    setMapping(autoMap(parsed[0], { withTrack }));
    setStep("map");
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([templateCsv({ withTrack })], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = withTrack ? "crm-import-template.csv" : "contacts-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const mapTo = (i, field) => setMapping((m) => {
    const next = { ...m };
    for (const k of Object.keys(next)) if (field && next[k] === field) next[k] = null; // one column per field
    next[i] = field || null;
    return next;
  });
  const hasName = Object.values(mapping).includes("name");

  const runImport = async () => {
    setBusy(true);
    const idFor = {}; // "row:<i>" → created contact id
    const usable = preview.filter((p) => p.kind !== "problem");
    const created = await runLimited(usable.filter((p) => p.kind === "new"), async (p) => {
      const r = await api.contacts.create(p.contact);
      if (r.ok) idFor[`row:${p.index}`] = r.data.id;
      else if (r.status === 409 && r.data?.existingId) idFor[`row:${p.index}`] = r.data.existingId;
      return r.ok || r.status === 409 ? { ok: true } : r;
    });
    let linked = 0;
    let trackFailed = 0;
    if (withTrack) {
      const linkable = usable.filter((p) => p.track && (p.kind === "existing" || idFor[`row:${p.index}`]));
      const res = await runLimited(linkable, (p) => {
        const contactId = p.kind === "new" ? idFor[`row:${p.index}`] : (idFor[p.existingId] || p.existingId);
        return api.crmTracks.create({ ...p.track, contactId, clientId });
      });
      linked = res.ok.filter(({ item }) => item.kind === "existing").length;
      trackFailed = res.failed.length;
    }
    const added = created.ok.length;
    // Skipped = problem rows + rows already in the book that get nothing new (no track to add).
    const skipped = preview.filter((p) => p.kind === "problem" || (p.kind === "existing" && !(withTrack && p.track))).length;
    const failed = created.failed.length + trackFailed;
    setBusy(false);
    onDone(`${added} added, ${linked} linked, ${skipped} skipped${failed ? `, ${failed} failed` : ""}`);
  };

  const counts = preview.reduce((c, p) => ({ ...c, [p.kind]: (c[p.kind] || 0) + 1 }), {});
  const cell = { padding: "6px 8px", borderBottom: `1px solid ${colors.border}`, fontSize: 12.5, color: colors.text, verticalAlign: "top" };
  return (
    <Modal title={withTrack ? `Import contacts to ${clientName || "this client"}` : "Import contacts"} width={step === "preview" ? 860 : 560} onClose={onClose}>
      {step === "file" && (
        <div>
          <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 0 }}>
            Upload a CSV with one contact per row. {withTrack ? "Each row is added to this client’s CRM as a Lead unless a Status column says otherwise." : ""}{" "}
            <button style={linkBtn} onClick={download}>Download template</button>
          </p>
          <label data-testid="csv-drop-zone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer?.files?.[0]); }}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "28px 16px", borderRadius: 10, cursor: "pointer", textAlign: "center",
              border: `2px dashed ${dragging ? colors.accent : colors.borderLight}`, background: dragging ? colors.accentDim : "transparent" }}>
            <span style={{ fontSize: 14, color: colors.text, fontWeight: 600 }}>{dragging ? "Drop to upload" : "Drag a CSV here"}</span>
            <span style={{ fontSize: 12.5, color: colors.textMuted }}>or click to choose a file</span>
            <input type="file" accept=".csv,text/csv" aria-label="CSV file" onChange={(e) => onFile(e.target.files?.[0])} style={{ display: "none" }} />
          </label>
          {error && <div style={{ color: colors.danger, fontSize: 12.5, marginTop: 8 }}>{error}</div>}
        </div>
      )}
      {step === "map" && (
        <div>
          <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 0 }}>Match your columns to ours. Columns left on “Don’t import” are ignored.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, alignItems: "center" }}>
            {headers.map((h, i) => (
              <React.Fragment key={i}>
                <span style={{ fontSize: 13, color: colors.text }}>{h || `(column ${i + 1})`}</span>
                <select aria-label={`Column ${h}`} style={inputStyle} value={mapping[i] || ""} onChange={(e) => mapTo(i, e.target.value)}>
                  <option value="">Don’t import</option>
                  {fields.map((f) => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                </select>
              </React.Fragment>
            ))}
          </div>
          {!hasName && <div style={{ color: colors.warning, fontSize: 12.5, marginTop: 8 }}>Pick the column that holds each person’s name.</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button onClick={() => setStep("file")} style={ghostBtn}>Back</button>
            <button disabled={!hasName} onClick={() => { setPreview(buildPreview(rows, mapping, { contacts, users, clientTracks, withTrack })); setStep("preview"); }}
              style={{ ...primaryBtn, opacity: hasName ? 1 : 0.5 }}>Preview</button>
          </div>
        </div>
      )}
      {step === "preview" && (
        <div>
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 10 }}>
            {counts.new || 0} new · {counts.existing || 0} already in book · {counts.problem || 0} with problems (skipped)
          </div>
          <div style={{ maxHeight: "50vh", overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["", "Name", "Organization", "Email", "Notes"].map((h) => <th key={h} style={{ ...cell, color: colors.textDim, textAlign: "left", fontWeight: 500 }}>{h}</th>)}</tr></thead>
              <tbody>
                {preview.map((p) => (
                  <tr key={p.index}>
                    <td style={cell}><span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap", color: KIND[p.kind].color, background: KIND[p.kind].bg }}>{KIND[p.kind].label}</span></td>
                    <td style={cell}>{p.contact.name || "—"}</td>
                    <td style={cell}>{p.contact.organization}</td>
                    <td style={cell}>{p.contact.email}</td>
                    <td style={{ ...cell, color: colors.textMuted }}>{[p.problem, ...p.warnings].filter(Boolean).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button onClick={() => setStep("map")} style={ghostBtn} disabled={busy}>Back</button>
            <button onClick={runImport} disabled={busy} style={primaryBtn}>{busy ? "Importing…" : `Import ${preview.length - (counts.problem || 0)} rows`}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
